import { createApi } from '@reduxjs/toolkit/query/react';

import { TaskUpdate } from '../types/client-intake-types';
import {
  ApproveMedicalProviderRequest,
  CheckInObjectives,
  CreateTreatmentBarrier,
  CreateTreatmentCheckInProviderUpdate,
  EncounterSchema,
  MedicalProvider,
  MergeProviderRequest,
  ReviewFoundProviderEntry,
  SaveCheckInDraftPayload,
  TreatmentBarrier,
  TreatmentCheckIn,
  TreatmentCheckInAIExtraction,
  TreatmentCheckInObjective,
  TreatmentCheckInProviderUpdate,
  UpdateTreatmentBarrier,
} from '../types/treatment-types';
import { baseQueryWithErrorHandling } from './baseQuery';

// Medical Providers API
export const medicalProvidersApiClient = createApi({
  reducerPath: 'medicalProvidersApi',
  baseQuery: baseQueryWithErrorHandling,
  tagTypes: [
    'MedicalProviders',
    'MedicalProvider',
    'ReviewFoundProviders',
  ] as const,
  endpoints: (builder) => ({
    getMedicalProviders: builder.query<MedicalProvider[], string>({
      query: (matterId) => `api/matters/${matterId}/medical-providers/`,
      providesTags: (result, error, matterId) => [
        { type: 'MedicalProviders', id: matterId },
      ],
    }),
    getReviewFoundProviders: builder.query<
      ReviewFoundProviderEntry[],
      { matterId: string; q?: string; limit?: number }
    >({
      query: ({ matterId, q, limit }) => ({
        url: `api/matters/${matterId}/medical-providers/review-found/`,
        method: 'GET',
        params: { q, limit },
      }),
      providesTags: (result, error, { matterId }) => [
        { type: 'ReviewFoundProviders', id: matterId },
      ],
    }),
    getMedicalProvider: builder.query<
      MedicalProvider,
      { matterId: string; providerId: string }
    >({
      query: ({ matterId, providerId }) => ({
        url: `api/matters/${matterId}/medical-providers/${providerId}/`,
        method: 'GET',
      }),
      providesTags: (result, error, { matterId, providerId }) => [
        { type: 'MedicalProvider', id: `${matterId}-${providerId}` },
      ],
    }),
    createMedicalProvider: builder.mutation<
      MedicalProvider,
      { matterId: string; provider: Partial<MedicalProvider> }
    >({
      query: ({ matterId, provider }) => ({
        url: `api/matters/${matterId}/medical-providers/`,
        method: 'POST',
        body: provider,
      }),
      invalidatesTags: (result, error, { matterId }) => [
        { type: 'MedicalProviders', id: matterId },
      ],
    }),
    createMedicalProviderFromProviderService: builder.mutation<
      MedicalProvider,
      { matterId: string; provider_service_id: string }
    >({
      query: ({ matterId, provider_service_id }) => ({
        url: `api/matters/${matterId}/medical-providers/from-provider-service/`,
        method: 'POST',
        body: { provider_service_id },
      }),
      invalidatesTags: (result, error, { matterId }) => [
        { type: 'MedicalProviders', id: matterId },
      ],
    }),
    updateMedicalProvider: builder.mutation<
      MedicalProvider,
      {
        matterId: string;
        providerId: string;
        provider: Partial<MedicalProvider>;
      }
    >({
      query: ({ matterId, providerId, provider }) => ({
        url: `api/matters/${matterId}/medical-providers/${providerId}/`,
        method: 'PATCH',
        body: provider,
      }),
      invalidatesTags: (result, error, { matterId, providerId }) => [
        { type: 'MedicalProviders', id: matterId },
        { type: 'MedicalProvider', id: `${matterId}-${providerId}` },
      ],
    }),
    inferMedicalProviderCareLevels: builder.mutation<
      { started: number },
      { matterId: string }
    >({
      query: ({ matterId }) => ({
        url: `api/matters/${matterId}/medical-providers/infer_care_levels/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { matterId }) => [
        { type: 'MedicalProviders', id: matterId },
      ],
    }),
    deleteMedicalProvider: builder.mutation<
      void,
      { matterId: string; providerId: string }
    >({
      query: ({ matterId, providerId }) => ({
        url: `api/matters/${matterId}/medical-providers/${providerId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { matterId }) => [
        { type: 'MedicalProviders', id: matterId },
      ],
    }),
    mergeMedicalProviders: builder.mutation<
      MedicalProvider,
      { matterId: string; mergeProviderRequest: MergeProviderRequest }
    >({
      query: ({ matterId, mergeProviderRequest }) => ({
        url: `api/matters/${matterId}/medical-providers/merge_providers/`,
        method: 'POST',
        body: mergeProviderRequest,
      }),
      invalidatesTags: (result, error, { matterId }) => [
        { type: 'MedicalProviders', id: matterId },
      ],
    }),
    // Provider lifecycle endpoints
    approveMedicalProvider: builder.mutation<
      MedicalProvider,
      {
        matterId: string;
        providerId: string;
        provider?: ApproveMedicalProviderRequest;
      }
    >({
      query: ({ matterId, providerId, provider }) => ({
        url: `api/matters/${matterId}/medical-providers/${providerId}/approve/`,
        method: 'POST',
        body: provider,
      }),
      invalidatesTags: (result, error, { matterId, providerId }) => [
        { type: 'MedicalProviders', id: matterId },
        { type: 'MedicalProvider', id: `${matterId}-${providerId}` },
        { type: 'ReviewFoundProviders', id: matterId },
      ],
    }),
    rejectMedicalProvider: builder.mutation<
      MedicalProvider,
      { matterId: string; providerId: string }
    >({
      query: ({ matterId, providerId }) => ({
        url: `api/matters/${matterId}/medical-providers/${providerId}/reject/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { matterId, providerId }) => [
        { type: 'MedicalProviders', id: matterId },
        { type: 'MedicalProvider', id: `${matterId}-${providerId}` },
        { type: 'ReviewFoundProviders', id: matterId },
      ],
    }),
    markProviderDoneTreating: builder.mutation<
      MedicalProvider,
      { matterId: string; providerId: string }
    >({
      query: ({ matterId, providerId }) => ({
        url: `api/matters/${matterId}/medical-providers/${providerId}/mark_done_treating/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { matterId, providerId }) => [
        { type: 'MedicalProviders', id: matterId },
        { type: 'MedicalProvider', id: `${matterId}-${providerId}` },
      ],
    }),
    markProviderStillTreating: builder.mutation<
      MedicalProvider,
      { matterId: string; providerId: string }
    >({
      query: ({ matterId, providerId }) => ({
        url: `api/matters/${matterId}/medical-providers/${providerId}/mark_still_treating/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { matterId, providerId }) => [
        { type: 'MedicalProviders', id: matterId },
        { type: 'MedicalProvider', id: `${matterId}-${providerId}` },
      ],
    }),
    uncullProvider: builder.mutation<
      MedicalProvider,
      { matterId: string; providerId: string }
    >({
      query: ({ matterId, providerId }) => ({
        url: `api/matters/${matterId}/medical-providers/${providerId}/uncull/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { matterId, providerId }) => [
        { type: 'MedicalProviders', id: matterId },
        { type: 'MedicalProvider', id: `${matterId}-${providerId}` },
      ],
    }),
    resetMedicalProviderStatus: builder.mutation<
      MedicalProvider,
      { matterId: string; providerId: string }
    >({
      query: ({ matterId, providerId }) => ({
        url: `api/matters/${matterId}/medical-providers/${providerId}/reset_status/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { matterId, providerId }) => [
        { type: 'MedicalProviders', id: matterId },
        { type: 'MedicalProvider', id: `${matterId}-${providerId}` },
        { type: 'ReviewFoundProviders', id: matterId },
      ],
    }),
    // Identity lifecycle endpoints (Unit 10)
    resolveProviderIdentity: builder.mutation<
      MedicalProvider,
      { matterId: string; providerId: string }
    >({
      query: ({ matterId, providerId }) => ({
        url: `api/matters/${matterId}/medical-providers/${providerId}/resolve-identity/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { matterId, providerId }) => [
        { type: 'MedicalProviders', id: matterId },
        { type: 'MedicalProvider', id: `${matterId}-${providerId}` },
      ],
    }),
    deferProviderIdentity: builder.mutation<
      MedicalProvider,
      {
        matterId: string;
        providerId: string;
        reason: string;
        deferDays?: number;
      }
    >({
      query: ({ matterId, providerId, reason, deferDays }) => ({
        url: `api/matters/${matterId}/medical-providers/${providerId}/defer-identity/`,
        method: 'POST',
        body: { reason, defer_days: deferDays ?? 14 },
      }),
      invalidatesTags: (result, error, { matterId, providerId }) => [
        { type: 'MedicalProviders', id: matterId },
        { type: 'MedicalProvider', id: `${matterId}-${providerId}` },
      ],
    }),
    relinkProviderService: builder.mutation<
      MedicalProvider,
      {
        matterId: string;
        providerId: string;
        providerServiceId: string;
      }
    >({
      query: ({ matterId, providerId, providerServiceId }) => ({
        url: `api/matters/${matterId}/medical-providers/${providerId}/relink-service/`,
        method: 'POST',
        body: { provider_service_id: providerServiceId },
      }),
      invalidatesTags: (result, error, { matterId, providerId }) => [
        { type: 'MedicalProviders', id: matterId },
        { type: 'MedicalProvider', id: `${matterId}-${providerId}` },
      ],
    }),
  }),
});

// Encounters API
export const encountersApiClient = createApi({
  reducerPath: 'encountersApi',
  baseQuery: baseQueryWithErrorHandling,
  tagTypes: ['Encounters', 'Encounter'] as const,
  endpoints: (builder) => ({
    getEncounters: builder.query<
      EncounterSchema[],
      { matterId: string; providerId: string }
    >({
      query: ({ matterId, providerId }) =>
        `api/matters/${matterId}/medical-providers/${providerId}/encounters/`,
      providesTags: (result, error, { matterId, providerId }) => [
        { type: 'Encounters', id: `${matterId}-${providerId}` },
      ],
    }),
    getEncounter: builder.query<
      EncounterSchema,
      { matterId: string; providerId: string; encounterId: string }
    >({
      query: ({ matterId, providerId, encounterId }) => ({
        url: `api/matters/${matterId}/medical-providers/${providerId}/encounters/${encounterId}/`,
        method: 'GET',
      }),
      providesTags: (result, error, { matterId, providerId, encounterId }) => [
        { type: 'Encounter', id: `${matterId}-${providerId}-${encounterId}` },
      ],
    }),
    createEncounter: builder.mutation<
      EncounterSchema,
      {
        matterId: string;
        providerId: string;
        encounter: Partial<EncounterSchema>;
      }
    >({
      query: ({ matterId, providerId, encounter }) => ({
        url: `api/matters/${matterId}/medical-providers/${providerId}/encounters/`,
        method: 'POST',
        body: encounter,
      }),
      invalidatesTags: (result, error, { matterId, providerId }) => [
        { type: 'Encounters', id: `${matterId}-${providerId}` },
      ],
    }),
    updateEncounter: builder.mutation<
      EncounterSchema,
      {
        matterId: string;
        providerId: string;
        encounterId: string;
        encounter: Partial<EncounterSchema>;
      }
    >({
      query: ({ matterId, providerId, encounterId, encounter }) => ({
        url: `api/matters/${matterId}/medical-providers/${providerId}/encounters/${encounterId}/`,
        method: 'PATCH',
        body: encounter,
      }),
      invalidatesTags: (
        result,
        error,
        { matterId, providerId, encounterId },
      ) => [
        { type: 'Encounters', id: `${matterId}-${providerId}` },
        { type: 'Encounter', id: `${matterId}-${providerId}-${encounterId}` },
      ],
    }),
    deleteEncounter: builder.mutation<
      void,
      { matterId: string; providerId: string; encounterId: string }
    >({
      query: ({ matterId, providerId, encounterId }) => ({
        url: `api/matters/${matterId}/medical-providers/${providerId}/encounters/${encounterId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (
        result,
        error,
        { matterId, providerId, encounterId },
      ): Array<
        { type: 'Encounters'; id: string } | { type: 'Encounter'; id: string }
      > => [
        { type: 'Encounters', id: `${matterId}-${providerId}` },
        { type: 'Encounter', id: `${matterId}-${providerId}-${encounterId}` },
      ],
    }),
    mergeEncounters: builder.mutation<
      EncounterSchema,
      { matterId: string; providerId: string; encounterIds: string[] }
    >({
      query: ({ matterId, providerId, encounterIds }) => ({
        url: `api/matters/${matterId}/medical-providers/${providerId}/encounters/merge/`,
        method: 'POST',
        body: { encounter_ids: encounterIds },
      }),
      invalidatesTags: (result, error, { matterId, providerId }) => [
        { type: 'Encounters', id: `${matterId}-${providerId}` },
      ],
    }),
  }),
});

// Treatment Check-Ins API
export const treatmentCheckInsApiClient = createApi({
  reducerPath: 'treatmentCheckInsApi',
  baseQuery: baseQueryWithErrorHandling,
  tagTypes: [
    'TreatmentCheckIns',
    'TreatmentCheckIn',
    'TreatmentCheckInTaskUpdates',
  ] as const,
  endpoints: (builder) => ({
    // List
    getTreatmentCheckIns: builder.query<TreatmentCheckIn[], string>({
      query: (matterId) => `api/matters/${matterId}/treatment-check-ins/`,
      providesTags: (result, error, matterId) => [
        { type: 'TreatmentCheckIns', id: matterId },
      ],
    }),

    // Retrieve
    getTreatmentCheckIn: builder.query<
      TreatmentCheckIn,
      { matterId: string; checkInId: string }
    >({
      query: ({ matterId, checkInId }) => ({
        url: `api/matters/${matterId}/treatment-check-ins/${checkInId}/`,
        method: 'GET',
      }),
      providesTags: (result, error, { matterId, checkInId }) => [
        { type: 'TreatmentCheckIn', id: `${matterId}-${checkInId}` },
      ],
    }),

    // Create draft check-in
    createTreatmentCheckIn: builder.mutation<
      TreatmentCheckIn,
      {
        matterId: string;
        userId?: string | null;
        triggered_by_task_id?: string | null;
      }
    >({
      query: ({ matterId, userId, triggered_by_task_id }) => ({
        url: `api/matters/${matterId}/treatment-check-ins/`,
        method: 'POST',
        body: {
          ...(userId && { user_id: userId }),
          ...(triggered_by_task_id && { triggered_by_task_id }),
        },
      }),
      invalidatesTags: (result, error, { matterId }) => [
        { type: 'TreatmentCheckIns', id: matterId },
      ],
    }),

    // Save draft
    saveTreatmentCheckInDraft: builder.mutation<
      TreatmentCheckIn,
      { matterId: string; checkInId: string; payload: SaveCheckInDraftPayload }
    >({
      query: ({ matterId, checkInId, payload }) => ({
        url: `api/matters/${matterId}/treatment-check-ins/${checkInId}/`,
        method: 'PATCH',
        body: payload,
      }),
      invalidatesTags: (result, error, { matterId, checkInId }) => [
        { type: 'TreatmentCheckIns', id: matterId },
        { type: 'TreatmentCheckIn', id: `${matterId}-${checkInId}` },
      ],
    }),

    // Finalize
    finalizeTreatmentCheckIn: builder.mutation<
      TreatmentCheckIn,
      { matterId: string; checkInId: string; payload: SaveCheckInDraftPayload }
    >({
      query: ({ matterId, checkInId, payload }) => ({
        url: `api/matters/${matterId}/treatment-check-ins/${checkInId}/finalize/`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (result, error, { matterId, checkInId }) => [
        { type: 'TreatmentCheckIns', id: matterId },
        { type: 'TreatmentCheckIn', id: `${matterId}-${checkInId}` },
      ],
    }),

    // Delete
    deleteTreatmentCheckIn: builder.mutation<
      void,
      { matterId: string; checkInId: string }
    >({
      query: ({ matterId, checkInId }) => ({
        url: `api/matters/${matterId}/treatment-check-ins/${checkInId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { matterId }) => [
        { type: 'TreatmentCheckIns', id: matterId },
      ],
    }),

    // Provider Updates
    addProviderUpdateToCheckIn: builder.mutation<
      TreatmentCheckInProviderUpdate,
      {
        matterId: string;
        checkInId: string;
        providerUpdate: CreateTreatmentCheckInProviderUpdate;
      }
    >({
      query: ({ matterId, checkInId, providerUpdate }) => ({
        url: `api/matters/${matterId}/treatment-check-ins/${checkInId}/provider-updates/`,
        method: 'POST',
        body: providerUpdate,
      }),
      invalidatesTags: (result, error, { matterId, checkInId }) => [
        { type: 'TreatmentCheckIns', id: matterId },
        { type: 'TreatmentCheckIn', id: `${matterId}-${checkInId}` },
      ],
    }),

    deleteProviderUpdateFromCheckIn: builder.mutation<
      void,
      { matterId: string; checkInId: string; providerUpdateId: string }
    >({
      query: ({ matterId, checkInId, providerUpdateId }) => ({
        url: `api/matters/${matterId}/treatment-check-ins/${checkInId}/provider-updates/${providerUpdateId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { matterId, checkInId }) => [
        { type: 'TreatmentCheckIns', id: matterId },
        { type: 'TreatmentCheckIn', id: `${matterId}-${checkInId}` },
      ],
    }),
    getTreatmentCheckInTaskUpdates: builder.query<
      TaskUpdate[],
      { matterId: string }
    >({
      query: ({ matterId }) =>
        `api/matters/${matterId}/treatment-check-ins-task-updates/`,
      providesTags: (result, error, { matterId }) => [
        { type: 'TreatmentCheckInTaskUpdates', id: matterId },
      ],
    }),

    // AI Extraction
    getAIExtraction: builder.query<
      TreatmentCheckInAIExtraction,
      { matterId: string; checkInId: string }
    >({
      query: ({ matterId, checkInId }) => ({
        url: `api/matters/${matterId}/treatment-check-ins/${checkInId}/ai-extraction/`,
        method: 'GET',
      }),
      providesTags: (result, error, { matterId, checkInId }) => [
        { type: 'TreatmentCheckIn', id: `${matterId}-${checkInId}` },
      ],
    }),

    // Generate structured objectives for a draft check-in
    generateCheckInObjectives: builder.mutation<
      TreatmentCheckInObjective[],
      { matterId: string; checkInId: string }
    >({
      query: ({ matterId, checkInId }) => ({
        url: `api/matters/${matterId}/treatment-check-ins/${checkInId}/generate-objectives/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { matterId, checkInId }) => [
        { type: 'TreatmentCheckIn', id: `${matterId}-${checkInId}` },
      ],
    }),

    // AI-Generated Objectives (legacy)
    getCheckInObjectives: builder.query<
      CheckInObjectives,
      { matterId: string; checkInId: string }
    >({
      query: ({ matterId, checkInId }) => ({
        url: `api/matters/${matterId}/treatment-check-ins/${checkInId}/objectives/`,
        method: 'GET',
      }),
      providesTags: (result, error, { matterId, checkInId }) => [
        { type: 'TreatmentCheckIn', id: `${matterId}-${checkInId}` },
      ],
    }),
  }),
});

// Treatment Barriers API
export const treatmentBarriersApiClient = createApi({
  reducerPath: 'treatmentBarriersApi',
  baseQuery: baseQueryWithErrorHandling,
  tagTypes: ['TreatmentBarriers', 'TreatmentBarrier'] as const,
  endpoints: (builder) => ({
    // List
    getTreatmentBarriers: builder.query<
      TreatmentBarrier[],
      { matterId: string; discoveredInCheckInId?: string }
    >({
      query: ({ matterId, discoveredInCheckInId }) => ({
        url: `api/matters/${matterId}/treatment-barriers/`,
        params: {
          ...(discoveredInCheckInId
            ? { discovered_in_check_in_id: discoveredInCheckInId }
            : {}),
        },
        method: 'GET',
      }),
      providesTags: (result, error, { matterId }) => [
        { type: 'TreatmentBarriers', id: matterId },
      ],
    }),

    // Retrieve
    getTreatmentBarrier: builder.query<
      TreatmentBarrier,
      { matterId: string; barrierId: string }
    >({
      query: ({ matterId, barrierId }) => ({
        url: `api/matters/${matterId}/treatment-barriers/${barrierId}/`,
        method: 'GET',
      }),
      providesTags: (result, error, { matterId, barrierId }) => [
        { type: 'TreatmentBarrier', id: `${matterId}-${barrierId}` },
      ],
    }),

    // Create
    createTreatmentBarrier: builder.mutation<
      TreatmentBarrier,
      { matterId: string; payload: CreateTreatmentBarrier }
    >({
      query: ({ matterId, payload }) => ({
        url: `api/matters/${matterId}/treatment-barriers/`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (result, error, { matterId }) => [
        { type: 'TreatmentBarriers', id: matterId },
      ],
    }),

    // Update (partial)
    updateTreatmentBarrier: builder.mutation<
      TreatmentBarrier,
      { matterId: string; barrierId: string; payload: UpdateTreatmentBarrier }
    >({
      query: ({ matterId, barrierId, payload }) => ({
        url: `api/matters/${matterId}/treatment-barriers/${barrierId}/`,
        method: 'PATCH',
        body: payload,
      }),
      invalidatesTags: (result, error, { matterId, barrierId }) => [
        { type: 'TreatmentBarriers', id: matterId },
        { type: 'TreatmentBarrier', id: `${matterId}-${barrierId}` },
      ],
    }),

    // Delete
    deleteTreatmentBarrier: builder.mutation<
      void,
      { matterId: string; barrierId: string }
    >({
      query: ({ matterId, barrierId }) => ({
        url: `api/matters/${matterId}/treatment-barriers/${barrierId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { matterId }) => [
        { type: 'TreatmentBarriers', id: matterId },
      ],
    }),
  }),
});

// Export hooks for usage in components
export const {
  useGetMedicalProvidersQuery,
  useGetReviewFoundProvidersQuery,
  useLazyGetReviewFoundProvidersQuery,
  useGetMedicalProviderQuery,
  useCreateMedicalProviderMutation,
  useCreateMedicalProviderFromProviderServiceMutation,
  useUpdateMedicalProviderMutation,
  useInferMedicalProviderCareLevelsMutation,
  useDeleteMedicalProviderMutation,
  useMergeMedicalProvidersMutation,
  useApproveMedicalProviderMutation,
  useRejectMedicalProviderMutation,
  useMarkProviderDoneTreatingMutation,
  useMarkProviderStillTreatingMutation,
  useUncullProviderMutation,
  useResetMedicalProviderStatusMutation,
  useResolveProviderIdentityMutation,
  useDeferProviderIdentityMutation,
  useRelinkProviderServiceMutation,
} = medicalProvidersApiClient;

export const {
  useGetEncountersQuery,
  useGetEncounterQuery,
  useCreateEncounterMutation,
  useUpdateEncounterMutation,
  useDeleteEncounterMutation,
  useMergeEncountersMutation,
} = encountersApiClient;

export const {
  useGetTreatmentCheckInsQuery,
  useGetTreatmentCheckInQuery,
  useCreateTreatmentCheckInMutation,
  useSaveTreatmentCheckInDraftMutation,
  useFinalizeTreatmentCheckInMutation,
  useDeleteTreatmentCheckInMutation,
  useAddProviderUpdateToCheckInMutation,
  useDeleteProviderUpdateFromCheckInMutation,
  useGetTreatmentCheckInTaskUpdatesQuery,
  useGetAIExtractionQuery,
  useGenerateCheckInObjectivesMutation,
  useGetCheckInObjectivesQuery,
} = treatmentCheckInsApiClient;

export const {
  useGetTreatmentBarriersQuery,
  useGetTreatmentBarrierQuery,
  useCreateTreatmentBarrierMutation,
  useUpdateTreatmentBarrierMutation,
  useDeleteTreatmentBarrierMutation,
} = treatmentBarriersApiClient;
